/**
 * Pure logic of the translations screen (port of TranslationsComponent):
 * section prefix of the platform, filtering and sorting of the rows.
 */
import { PLATFORM, type Platform } from '@/shared/constants';
import type { Translation } from './api';

/** Sections reserved to a platform; rows of these sections are hidden on ABCDior. */
export const SECTION_PREFIXES = ['DMS_'] as const;

export const sectionPrefixOf = (platform: Platform): string =>
  platform === PLATFORM.DMS ? 'DMS_' : '';

export type SortKey = 'section' | 'key' | 'value';

export interface SortState {
  by: SortKey;
  reverse: boolean;
}

export const DEFAULT_SORT: SortState = { by: 'section', reverse: false };

export const TRANSLATION_QUERY_PARAMETERS = {
  search: 'search',
  sectionFilter: 'sectionFilter',
} as const;

export interface TranslationFilters {
  search: string;
  sectionFilter: string;
}

export const parseTranslationFilters = (
  query: string,
): TranslationFilters => {
  const parameters = new URLSearchParams(query);
  return {
    search: parameters.get(TRANSLATION_QUERY_PARAMETERS.search) ?? '',
    sectionFilter:
      parameters.get(TRANSLATION_QUERY_PARAMETERS.sectionFilter) ?? '',
  };
};

export const nextSort = (current: SortState, by: SortKey): SortState =>
  current.by === by
    ? { by, reverse: !current.reverse }
    : { by, reverse: false };

const normalize = (value: unknown): string => {
  if (typeof value === 'string') return value;
  return value == null ? '' : String(value);
};

const collator = new Intl.Collator();

/**
 * Rows to display: sorted with Intl.Collator (accents aware), reversed on
 * demand, then limited to the sections of the platform: those starting with
 * its prefix, or those with no reserved prefix at all for ABCDior.
 */
export const displayedTranslations = (
  items: readonly Translation[],
  sort: SortState,
  sectionPrefix: string,
  search = '',
  sectionFilter = '',
): Translation[] => {
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const sorted = [...items].sort((a, b) =>
    collator.compare(normalize(a[sort.by]), normalize(b[sort.by])),
  );
  if (sort.reverse) sorted.reverse();
  return sorted.filter((item) => {
    const section = item.section ?? '';
    const inPlatform = sectionPrefix
      ? section.startsWith(sectionPrefix)
      : !SECTION_PREFIXES.some((prefix) => section.startsWith(prefix));
    return (
      inPlatform &&
      (!sectionFilter || section === sectionFilter) &&
      normalize(item.key).toLocaleLowerCase().includes(normalizedSearch)
    );
  });
};

/** The table always shows at least five rows. */
export const MIN_ROWS = 5;

export const fillerRowCount = (displayed: number): number =>
  Math.max(0, MIN_ROWS - displayed);
